import React, { useState, useEffect } from 'react';
import { env } from '@/config/env';
import {
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Clock,
  HardDrive,
  MessageSquare,
  Users,
  RefreshCw,
  ShieldCheck
} from 'lucide-react';
import Card from '@shared/Card';
import Button from '@shared/Button';
import api from '@/utils/api';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { formatStorageDisplayString } from '@shared-utils/formatStorage';

interface QuotaInfo {
  withinLimit: boolean;
  used: number;
  limit: number;
  percentage: number;
  severity: 'ok' | 'warning' | 'critical';
}

interface LicenseInfo {
  key: string;
  organization: string;
  type: 'perpetual' | 'subscription' | 'trial';
  status: 'active' | 'grace_period' | 'expired';
  expiresAt: string;
  daysRemaining: number;
}

interface LicenseFeatures {
  channels: string[];
  languages: string[];
  customBranding: boolean;
  roleBasedAccess: boolean;
  advancedAnalytics: boolean;
  apiAccess: boolean;
  prioritySupport: boolean;
}

interface LicenseData {
  license: LicenseInfo;
  quotas: {
    storage: QuotaInfo;
    queries: QuotaInfo;
    users: QuotaInfo;
  };
  features: LicenseFeatures;
  warnings?: string[];
}

const LicenseStatusPage: React.FC = () => {
  const [licenseData, setLicenseData] = useState<LicenseData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLicenseStatus();
  }, []);

  const fetchLicenseStatus = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const data = await api.getLicenseStatus();
      console.log(data);

      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch license status');
      }

      setLicenseData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load license information');
      console.error('Failed to fetch license status:', err);
    } finally {
      setLoading(false);
    }
  };

  const syncConfigAndUsage = async (): Promise<void> => {
    if(!env.isLicenseOffline) {
      try {
        setSyncing(true);
        setError(null);
        await api.triggerConfigAndUsageSync();
        toast.success('License configuration and usage synced successfully');
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to sync configuration and usage';
        // setError(errorMessage);
        toast.error(errorMessage);
        console.error('Sync error:', err);
      } finally {
        setSyncing(false);
        await fetchLicenseStatus();
      }
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-8 h-8 text-green-500" />;
      case 'grace_period':
        return <AlertTriangle className="w-8 h-8 text-yellow-500" />;
      case 'expired':
        return <AlertCircle className="w-8 h-8 text-red-500" />;
      default:
        return <Clock className="w-8 h-8 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'grace_period':
        return 'bg-yellow-100 text-yellow-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getQuotaColor = (severity: string): string => {
    switch (severity) {
      case 'ok':
        return 'bg-green-500';
      case 'warning':
        return 'bg-yellow-500';
      case 'critical':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error || !licenseData) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-end">
          <Button onClick={() => navigate('/license/activate')} className="flex items-center mt-4 mb-6 h-12">
            <ShieldCheck className="w-5 h-5 text-success" /> Activate License
          </Button>
        </div>
        <Card className="p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load License</h2>
          <p className="text-gray-600 mb-4">{error || 'No license information available'}</p>
          { !env.isLicenseOffline && (
            <div className="flex items-center justify-center">
              <Button
                onClick={syncConfigAndUsage}
                disabled={syncing}
                className="flex items-center mt-4 mb-6 h-12"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Refresh'}
              </Button>
            </div>
          )}
        </Card>
      </div>
    );
  }

  const { license, quotas, features, warnings } = licenseData;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">License Status</h1>
          <p className="text-gray-600 mt-1">Manage your SmartDocs AI license</p>
        </div>
        { !env.isLicenseOffline && (
          <Button
            variant="secondary"
            onClick={syncConfigAndUsage}
            disabled={syncing}
            className="flex items-center mt-4 mb-6 h-12"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Refresh'}
          </Button>
        )}
      </div>

      {/* Warnings */}
      {warnings && warnings.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-yellow-800">Attention Required</h3>
              <ul className="mt-1 text-sm text-yellow-700 list-disc list-inside">
                {warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* License Info Card */}
        <Card className="p-6">
          <div className="flex items-start gap-4 mb-6">
            {getStatusIcon(license.status)}
            <div>
              <h2 className="text-lg font-semibold text-gray-900">License Information</h2>
              <span
                className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-1 ${getStatusColor(license.status)}`}
              >
                {license.status.replace('_', ' ').toUpperCase()}
              </span>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Organization</span>
              <span className="font-medium text-gray-900">{license.organization}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">License Key</span>
              <code className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{license.key}</code>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">License Type</span>
              <span className="font-medium text-gray-900 capitalize">{license.type}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Expires</span>
              <span className="font-medium text-gray-900">{formatDate(license.expiresAt)}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Days Remaining</span>
              <span
                className={`font-bold ${license.daysRemaining <= 30 ? 'text-red-600' : 'text-green-600'}`}
              >
                {license.daysRemaining} days
              </span>
            </div>
          </div>

          {license.daysRemaining <= 30 && (
            <div className="mt-6">
              <Button className="w-full h-12">Renew License</Button>
            </div>
          )}
        </Card>

        {/* Quota Usage Card */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-15">Usage & Quotas</h2>

          <div className="space-y-6">
            {/* Storage */}
            <div className="mb-10">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <HardDrive className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Storage</span>
                </div>
                <span className="text-sm text-gray-600">
                  {formatStorageDisplayString(quotas.storage.used || 0, quotas.storage.limit || 0)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${getQuotaColor(quotas.storage.severity)}`}
                  style={{ width: `${Math.min(quotas.storage.percentage, 100)}%` }}
                />
              </div>
            </div>

            {/* Queries */}
            <div className="mb-10">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Queries This Month</span>
                </div>
                <span className="text-sm text-gray-600">
                  {quotas.queries.used.toLocaleString()} / {quotas.queries.limit.toLocaleString()}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${getQuotaColor(quotas.queries.severity)}`}
                  style={{ width: `${Math.min(quotas.queries.percentage, 100)}%` }}
                />
              </div>
            </div>

            {/* Users */}
            <div className="mb-10">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Active Users</span>
                </div>
                <span className="text-sm text-gray-600">
                  {quotas.users.used} / {quotas.users.limit}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${getQuotaColor(quotas.users.severity)}`}
                  style={{ width: `${Math.min(quotas.users.percentage, 100)}%` }}
                />
              </div>
            </div>

            <div className="flex items-center">
              <Button onClick={() => navigate('/license/activate')} className="flex items-center justify-center gap-1 w-full mt-7 h-12">
                <ShieldCheck className="w-5 h-5 text-success" /> Change License
              </Button>
            </div>

          </div>
        </Card>
      </div>

      {/* Features Card */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Licensed Features</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <FeatureItem label="Custom Branding" enabled={features.customBranding} />
          <FeatureItem label="Role-Based Access" enabled={features.roleBasedAccess} />
          <FeatureItem label="Advanced Analytics" enabled={features.advancedAnalytics} />
          <FeatureItem label="API Access" enabled={features.apiAccess} />
          <FeatureItem label="Priority Support" enabled={features.prioritySupport} />
          <div className="col-span-2">
            <div className="text-sm text-gray-600">
              <span className="font-medium">Channels:</span> {features.channels.join(', ')}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              <span className="font-medium">Languages:</span> {features.languages.join(', ')}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

const FeatureItem: React.FC<{ label: string; enabled: boolean }> = ({ label, enabled }) => (
  <div className="flex items-center gap-2">
    {enabled ? (
      <CheckCircle className="w-5 h-5 text-green-500" />
    ) : (
      <AlertCircle className="w-5 h-5 text-gray-300" />
    )}
    <span className={`text-sm ${enabled ? 'text-gray-900' : 'text-gray-400'}`}>{label}</span>
  </div>
);

export default LicenseStatusPage;
